import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import { Eye, EyeOff, Shield, ArrowRight, Loader, CheckCircle, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthPage = ({ mode = 'login' }) => {
  usePageTitle(mode === 'login' ? 'Sign In' : 'Register');
  const { login, register, user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [success, setSuccess] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: ''
  });

  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/portal');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const u = await login(form.email, form.password);
        navigate(u.role === 'admin' ? '/admin' : '/portal');
      } else {
        if (!form.first_name || !form.last_name) {
          setError(t('fieldRequired'));
          setLoading(false);
          return;
        }
        if (form.password.length < 8) {
          setError(t('invalidPassword'));
          setLoading(false);
          return;
        }
        await register(form);
        navigate('/portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password Modal
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#F8FAFC]" style={{ paddingTop: '72px' }}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-floating border border-gray-100">
            {forgotSuccess ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Check Your Email</h2>
                <p className="text-[#475569] mb-6">
                  If an account exists for <strong>{forgotEmail}</strong>, we've sent password reset instructions.
                </p>
                <button
                  onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); setForgotEmail(''); }}
                  className="w-full h-12 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2"
                  style={{ background: '#0B1F3A' }}
                >
                  <ArrowLeft size={18} /> Back to Login
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="flex items-center gap-2 text-[#475569] hover:text-[#0B1F3A] mb-6 transition-colors"
                >
                  <ArrowLeft size={18} /> Back to Login
                </button>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Mail size={32} className="text-[#2563EB]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Forgot Password?</h2>
                  <p className="text-[#475569] text-sm">
                    Enter your email address and we'll send you instructions to reset your password.
                  </p>
                </div>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-5">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#0B1F3A] mb-1.5 uppercase tracking-wide">Email Address</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      placeholder="Enter your email"
                      className="w-full h-12 px-4 bg-[#F8FAFC] border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full h-12 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ background: '#0B1F3A' }}
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? <><Loader size={18} className="animate-spin" /> Sending...</> : <>Send Reset Link <ArrowRight size={18} /></>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ paddingTop: '72px' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col justify-center items-center p-16 relative overflow-hidden" style={{ background: '#0B1F3A' }}>
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: '#D4AF37', filter: 'blur(60px)' }} />
        <div className="relative z-10 text-center max-w-sm">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: '#D4AF37' }}>
            <Shield size={40} className="text-[#0B1F3A]" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            {isLogin ? t('welcomeBack') : t('joinOurPortal')}
          </h2>
          <p className="text-blue-200 leading-relaxed mb-8">
            {isLogin
              ? (lang === 'es' ? 'Acceda a su portal seguro de cliente para gestionar documentos, citas y comunicaciones.' : 'Access your secure client portal to manage documents, appointments, and communications.')
              : (lang === 'es' ? 'Cree su cuenta segura para comenzar a gestionar sus necesidades fiscales con confianza.' : 'Create your secure account to start managing your tax needs with confidence.')
            }
          </p>
          <div className="space-y-4">
            {(lang === 'es' ? [
              'Carga y almacenamiento seguro de documentos',
              'Actualizaciones de estado en tiempo real',
              'Programar y gestionar citas',
              'Mensajería encriptada con su asesor',
              'Pagar facturas de forma segura en línea',
            ] : [
              'Secure document upload & storage',
              'Real-time filing status updates',
              'Schedule and manage appointments',
              'Encrypted messaging with your advisor',
              'Pay invoices securely online',
            ]).map((f) => (
              <div key={f} className="flex items-center gap-3 text-left">
                <CheckCircle size={18} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <span className="text-blue-200 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-8 text-center">
          <div className="text-blue-300 text-xs">{t('irsAuthorized')} · {t('allStates')}</div>
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-floating border border-gray-100">
            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">
                {isLogin ? t('signInToPortal') : t('createAccount')}
              </h1>
              <p className="text-[#475569] text-sm">
                {isLogin ? t('accessSecureDashboard') : t('startTaxJourney')}
              </p>
            </div>

            {/* Google Sign-In Button */}
            <button
              onClick={handleGoogleSignIn}
              className="w-full h-12 rounded-xl font-semibold text-[#0B1F3A] bg-white border-2 border-gray-200 hover:border-[#0B1F3A] hover:bg-[#F8FAFC] transition-all duration-200 flex items-center justify-center gap-3 mb-6"
              data-testid="google-signin-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-[#94A3B8]">or continue with email</span>
              </div>
            </div>

            {/* Tab Toggle */}
            <div className="flex bg-[#F1F5F9] rounded-xl p-1 mb-5">
              <button
                onClick={() => { setIsLogin(true); setError(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${isLogin ? 'bg-white text-[#0B1F3A] shadow-sm' : 'text-[#475569] hover:text-[#0B1F3A]'}`}
                data-testid="login-tab"
              >
                {t('signIn')}
              </button>
              <button
                onClick={() => { setIsLogin(false); setError(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${!isLogin ? 'bg-white text-[#0B1F3A] shadow-sm' : 'text-[#475569] hover:text-[#0B1F3A]'}`}
                data-testid="register-tab"
              >
                {t('createAccount')}
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-5" data-testid="auth-error">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm mb-5" data-testid="auth-success">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#0B1F3A] mb-1.5 uppercase tracking-wide">{t('firstName')}</label>
                    <input
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      required={!isLogin}
                      placeholder={lang === 'es' ? 'María' : 'Jane'}
                      className="w-full h-12 px-4 bg-[#F8FAFC] border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#0B1F3A] mb-1.5 uppercase tracking-wide">{t('lastName')}</label>
                    <input
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      required={!isLogin}
                      placeholder={lang === 'es' ? 'García' : 'Smith'}
                      className="w-full h-12 px-4 bg-[#F8FAFC] border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] mb-1.5 uppercase tracking-wide">{t('email')}</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder={lang === 'es' ? 'maria@ejemplo.com' : 'jane@example.com'}
                  className="w-full h-12 px-4 bg-[#F8FAFC] border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                  data-testid="email-input"
                />
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-xs font-semibold text-[#0B1F3A] mb-1.5 uppercase tracking-wide">{t('phone')} ({t('optional')})</label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(770) 000-0000"
                    className="w-full h-12 px-4 bg-[#F8FAFC] border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                    data-testid="phone-input"
                  />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide">{t('password')}</label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-[#2563EB] hover:underline font-semibold"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    name="password"
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange}
                    required
                    placeholder={isLogin ? (lang === 'es' ? 'Su contraseña' : 'Your password') : t('passwordMin')}
                    className="w-full h-12 px-4 pr-12 bg-[#F8FAFC] border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569] transition-colors"
                    data-testid="toggle-password-btn"
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: '#0B1F3A' }}
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <><Loader size={18} className="animate-spin" /> {t('processing')}</>
                ) : (
                  <>{isLogin ? t('signIn') : t('createAccount')} <ArrowRight size={18} /></>
                )}
              </button>
            </form>

            <div className="mt-5 text-center text-sm text-[#475569]">
              {isLogin ? (
                <>{t('dontHaveAccount')}{' '}
                  <button onClick={() => setIsLogin(false)} className="font-semibold text-[#2563EB] hover:underline" data-testid="switch-to-register">
                    {t('createOneFree')}
                  </button>
                </>
              ) : (
                <>{t('alreadyHaveAccount')}{' '}
                  <button onClick={() => setIsLogin(true)} className="font-semibold text-[#2563EB] hover:underline" data-testid="switch-to-login">
                    {t('signIn')}
                  </button>
                </>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
              <Shield size={14} />
              <span>{t('authSecure')}</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#475569]">
              {lang === 'es' ? '¿Necesita ayuda? Llámenos al' : 'Need help? Call us at'}{' '}
              <a href="tel:7702356999" className="font-semibold text-[#0B1F3A] hover:underline">770-235-6999</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
